import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";

/**
 * USUÁRIOS & ASSINATURAS.
 *
 * Além de ver quem paga, aqui ficam as AÇÕES que o CMS antigo tinha (criar, editar,
 * tornar admin, resetar senha, excluir). Sem elas, trocar o painel antigo por este
 * faria a equipe perder trabalho — e ela só descobriria na hora do aperto.
 *
 * As ações destrutivas PARECEM destrutivas: excluir pede o email digitado, e virar
 * admin pede confirmação. Dar privilégio de admin sem querer é tão ruim quanto apagar.
 */

const COR_STATUS = {
  ativo: "adm__chip--green",
  inadimplente: "adm__chip--orange",
  cancelado: "adm__chip--red",
  expirado: "adm__chip--red",
  gratuito: "",
};

const ORIGEM = { greenn: "Greenn", app_store: "App Store", play_store: "Play Store" };

export default function Users() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState("");
  const [plano, setPlano] = useState("");
  const [page, setPage] = useState(1);
  const [detalhe, setDetalhe] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // { titulo, texto, exigir, onOk, perigo }
  const [form, setForm] = useState(null); // { id?, name, email, telefone }

  const carregar = useCallback(
    (p = page) =>
      rodar(async () =>
        setDados(
          await apiGet(
            `${API_ROUTES.ADMIN.subscribers()}?${new URLSearchParams({
              search: busca,
              ...(plano ? { plan: plano } : {}),
              page: p,
              size: 25,
            })}`
          )
        )
      ),
    [busca, plano, page, rodar]
  );

  useEffect(() => {
    carregar(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      carregar(1);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const abrir = (u) =>
    rodar(async () => setDetalhe(await apiGet(API_ROUTES.ADMIN.userDetail(u.id))));

  const recarregar = async () => {
    await carregar();
    if (detalhe) setDetalhe(await apiGet(API_ROUTES.ADMIN.userDetail(detalhe.id)));
  };

  const rodarExpiracao = () =>
    rodar(async () => {
      const r = await apiSend(API_ROUTES.ADMIN.expiryRun(), "POST");
      await carregar();
      return r;
    }, "Expiração executada.");

  const totalPag = Math.max(1, Math.ceil((dados?.count || 0) / 25));

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Usuários & Assinaturas"
        subtitulo="Quem paga, por qual origem, e até quando."
        acoes={
          <button className="adm__btnGhost" onClick={rodarExpiracao}>
            Rodar expiração
          </button>
        }
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <input
            className="adm__input"
            placeholder="Buscar por nome, email, telefone ou CPF…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1 }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            {[["", "Todos"], ["pago", "Pagos"], ["gratuito", "Gratuitos"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setPlano(k)}
                className={plano === k ? "adm__btn" : "adm__btnGhost"}
              >
                {l}
              </button>
            ))}
          </div>
          <button className="adm__btn" onClick={() => setForm({ name: "", email: "", telefone: "" })}>
            + Novo usuário
          </button>
        </div>

        <div className="adm__card" style={{ overflow: "hidden" }}>
          <table className="adm__tabela">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th style={{ width: 130 }}>Telefone</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 230 }}>Planos</th>
                <th style={{ width: 90, textAlign: "right" }}>Transações</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.users || []).map((u) => (
                <tr key={u.id} onClick={() => abrir(u)} style={{ cursor: "pointer" }}>
                  <td style={{ fontWeight: 600 }}>
                    {u.name || "—"}
                    {u.role === "admin" && (
                      <span className="adm__chip adm__chip--red" style={{ marginLeft: 7 }}>
                        admin
                      </span>
                    )}
                  </td>
                  <td style={{ color: "var(--text2)" }}>{u.email}</td>
                  <td style={{ color: "var(--text2)" }}>{u.telefone || "—"}</td>
                  <td>
                    <span className={`adm__chip ${COR_STATUS[u.status] || ""}`}>{u.status}</span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(u.subscriptions || [])
                        .filter((s) => s !== "free")
                        .slice(0, 4)
                        .map((s) => (
                          <span key={s} className="adm__chip adm__chip--blue">
                            {s}
                          </span>
                        ))}
                      {(u.subscriptions || []).filter((s) => s !== "free").length > 4 && (
                        <span className="adm__chip">
                          +{(u.subscriptions || []).filter((s) => s !== "free").length - 4}
                        </span>
                      )}
                      {(u.subscriptions || []).filter((s) => s !== "free").length === 0 && (
                        <span style={{ color: "var(--text3)" }}>—</span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "right", color: "var(--text2)" }}>
                    {u.transactionsCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {dados && dados.users.length === 0 && (
            <p className="adm__vazio">Nenhum usuário encontrado.</p>
          )}
        </div>

        {dados && dados.count > 25 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 9, marginTop: 16, alignItems: "center" }}>
            <button
              className="adm__btnGhost"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
                carregar(page - 1);
              }}
            >
              ← Anterior
            </button>
            <span style={{ fontSize: 12.5, color: "var(--text2)" }}>
              {page} de {totalPag} · {dados.count.toLocaleString("pt-BR")} usuários
            </span>
            <button
              className="adm__btnGhost"
              disabled={page >= totalPag}
              onClick={() => {
                setPage(page + 1);
                carregar(page + 1);
              }}
            >
              Próxima →
            </button>
          </div>
        )}
      </div>

      {detalhe && (
        <Drawer
          u={detalhe}
          onFechar={() => setDetalhe(null)}
          onEditar={() =>
            setForm({
              id: detalhe.id,
              name: detalhe.name || "",
              email: detalhe.email,
              telefone: detalhe.telefone || "",
            })
          }
          onAdmin={() =>
            setConfirmar({
              titulo: detalhe.role === "admin" ? "Remover administrador" : "Tornar administrador",
              texto:
                detalhe.role === "admin"
                  ? `${detalhe.email} deixará de ter acesso a este painel.`
                  : `${detalhe.email} passará a ter acesso TOTAL a este painel — incluindo excluir usuários e mexer no acervo.`,
              onOk: () =>
                rodar(async () => {
                  await apiSend(API_ROUTES.USERS.update(detalhe.id), "PUT", {
                    role: detalhe.role === "admin" ? "user" : "admin",
                  });
                  setConfirmar(null);
                  await recarregar();
                }, "Permissão alterada."),
            })
          }
          onSenha={() =>
            setConfirmar({
              titulo: "Resetar senha",
              texto: `A senha de ${detalhe.email} será trocada por uma senha padrão. Ele precisará defini-la de novo para entrar.`,
              onOk: () =>
                rodar(async () => {
                  await apiSend(API_ROUTES.FORGOT_PASSWORD.defaultReset(), "POST", {
                    email: detalhe.email,
                  });
                  setConfirmar(null);
                }, "Senha resetada."),
            })
          }
          onExcluir={() =>
            setConfirmar({
              perigo: true,
              titulo: "Excluir usuário",
              texto: `Isto remove ${detalhe.email} da plataforma. As transações dele continuam no histórico, mas ele perde o acesso.`,
              exigir: detalhe.email,
              onOk: () =>
                rodar(async () => {
                  await apiSend(API_ROUTES.USERS.delete(detalhe.id), "DELETE");
                  setConfirmar(null);
                  setDetalhe(null);
                  await carregar();
                }, "Usuário excluído."),
            })
          }
        />
      )}

      {confirmar && <Confirmar {...confirmar} onCancelar={() => setConfirmar(null)} />}

      {form && (
        <FormUsuario
          form={form}
          onFechar={() => setForm(null)}
          onSalvar={(d) =>
            rodar(async () => {
              if (form.id) await apiSend(API_ROUTES.USERS.update(form.id), "PUT", d);
              else await apiSend(API_ROUTES.USERS.create(), "POST", d);
              setForm(null);
              await recarregar();
            }, form.id ? "Usuário atualizado." : "Usuário criado.")
          }
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

function Drawer({ u, onFechar, onEditar, onAdmin, onSenha, onExcluir }) {
  const agora = new Date();
  return (
    <div className="adm__drawerFundo" onClick={onFechar}>
      <div className="adm__drawer" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>{u.name || "(sem nome)"}</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text2)" }}>{u.email}</p>
          </div>
          <button className="adm__btnGhost" onClick={onFechar} style={{ padding: "5px 10px" }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 6, margin: "14px 0 20px", flexWrap: "wrap" }}>
          <span className={`adm__chip ${COR_STATUS[u.status] || ""}`}>{u.status}</span>
          {u.role === "admin" && <span className="adm__chip adm__chip--red">administrador</span>}
          {u.telefone && <span className="adm__chip">{u.telefone}</span>}
          {u.cpf && <span className="adm__chip">{u.cpf}</span>}
        </div>

        {/* ---------- assinaturas ---------- */}
        <h4 style={{ fontSize: 11, letterSpacing: 0.8, color: "var(--text3)", margin: "0 0 9px", textTransform: "uppercase" }}>
          Assinaturas
        </h4>
        {(u.subscriptionRecords || []).length === 0 && (
          <p style={{ fontSize: 13, color: "var(--text3)", marginTop: 0 }}>Nenhuma assinatura registrada.</p>
        )}
        {(u.subscriptionRecords || []).map((s) => {
          const venceu = s.currentPeriodEnd && new Date(s.currentPeriodEnd) < agora;
          return (
            <div key={s.id} className="adm__card" style={{ padding: 13, marginBottom: 8, background: "var(--elev)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                <strong style={{ fontSize: 13.5 }}>{s.productId}</strong>
                <span className={`adm__chip ${COR_STATUS[s.status] || ""}`}>{s.status}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)" }}>
                {ORIGEM[s.source] || s.source} ·{" "}
                {s.currentPeriodEnd ? (
                  <span style={{ color: venceu ? "var(--orange)" : undefined }}>
                    {venceu ? "venceu em " : "expira em "}
                    {new Date(s.currentPeriodEnd).toLocaleDateString("pt-BR")}
                  </span>
                ) : (
                  "não expira"
                )}
              </div>
            </div>
          );
        })}

        {/* ---------- transações ---------- */}
        <h4 style={{ fontSize: 11, letterSpacing: 0.8, color: "var(--text3)", margin: "20px 0 9px", textTransform: "uppercase" }}>
          Linha do tempo de transações
        </h4>
        {(u.transactions || []).length === 0 && (
          <p style={{ fontSize: 13, color: "var(--text3)", marginTop: 0 }}>Nenhuma transação registrada.</p>
        )}
        {(u.transactions || []).map((t) => (
          <div key={t.id} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid var(--line)", fontSize: 12.5 }}>
            <div>
              <strong>{t.event}</strong>
              <div style={{ color: "var(--text3)", fontSize: 11.5 }}>
                {ORIGEM[t.source] || t.source} · {t.productId}
              </div>
            </div>
            <div style={{ textAlign: "right", color: "var(--text2)" }}>
              {t.amount ? `R$ ${Number(t.amount).toFixed(2).replace(".", ",")}` : "—"}
              <div style={{ color: "var(--text3)", fontSize: 11 }}>
                {new Date(t.createdAt).toLocaleDateString("pt-BR")}
              </div>
            </div>
          </div>
        ))}

        {/* ---------- ações ---------- */}
        <h4 style={{ fontSize: 11, letterSpacing: 0.8, color: "var(--text3)", margin: "24px 0 10px", textTransform: "uppercase" }}>
          Ações
        </h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button className="adm__btnGhost" onClick={onEditar}>Editar dados</button>
          <button className="adm__btnGhost" onClick={onAdmin}>
            {u.role === "admin" ? "Remover administrador" : "Tornar administrador"}
          </button>
          <button className="adm__btnGhost" onClick={onSenha}>Resetar senha</button>
          <button
            className="adm__btnGhost"
            onClick={onExcluir}
            style={{ color: "var(--cancel)", borderColor: "rgba(255,90,95,.35)", marginTop: 8 }}
          >
            Excluir usuário
          </button>
        </div>
      </div>
    </div>
  );
}

/** Ação destrutiva PARECE destrutiva: pede o email digitado. */
function Confirmar({ titulo, texto, exigir, perigo, onOk, onCancelar }) {
  const [digitado, setDigitado] = useState("");
  const ok = !exigir || digitado.trim().toLowerCase() === exigir.toLowerCase();

  return (
    <div className="adm__modalFundo" onClick={onCancelar} style={{ zIndex: 200 }}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(420px,100%)" }}>
        <h3 style={{ color: perigo ? "var(--cancel)" : undefined }}>{titulo}</h3>
        <p className="adm__modalHint">{texto}</p>

        {exigir && (
          <div className="adm__campo">
            <label>
              Para confirmar, digite <code className="adm__code">{exigir}</code>
            </label>
            <input className="adm__input" value={digitado} onChange={(e) => setDigitado(e.target.value)} />
          </div>
        )}

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onCancelar}>Cancelar</button>
          <button
            className={`adm__btn ${perigo ? "adm__btnDanger" : ""}`}
            disabled={!ok}
            onClick={onOk}
          >
            {titulo}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormUsuario({ form, onFechar, onSalvar }) {
  const [d, setD] = useState(form);
  const valido = d.email?.trim();

  return (
    <div className="adm__modalFundo" onClick={onFechar} style={{ zIndex: 200 }}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()}>
        <h3>{form.id ? "Editar usuário" : "Novo usuário"}</h3>
        <p className="adm__modalHint">
          {form.id
            ? "Alterar o email muda o login desta pessoa."
            : "O usuário recebe um email para definir a senha."}
        </p>

        {[
          ["name", "Nome"],
          ["email", "Email"],
          ["telefone", "Telefone"],
        ].map(([k, l]) => (
          <div className="adm__campo" key={k}>
            <label>{l}</label>
            <input
              className="adm__input"
              value={d[k] || ""}
              onChange={(e) => setD({ ...d, [k]: e.target.value })}
            />
          </div>
        ))}

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>Cancelar</button>
          <button className="adm__btn" disabled={!valido} onClick={() => onSalvar(d)}>
            {form.id ? "Salvar" : "Criar usuário"}
          </button>
        </div>
      </div>
    </div>
  );
}
